import { useState, useEffect, useRef } from "react";
import PersianDate from "persian-date";
import "./JalaliDatePicker.scss";

function JalaliDatePicker({
  value,
  onChange,
  label,
  placeholder,
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(value || "");

  const [currentMonth, setCurrentMonth] = useState(() => {
    if (value) {
      const pd = new PersianDate(value);

      return {
        year: pd.year(),
        month: pd.month(),
      };
    }

    const now = new PersianDate();

    return {
      year: now.year(),
      month: now.month(),
    };
  });

  const inputRef = useRef(null);
  const popupRef = useRef(null);

  const months = [
    "فروردین",
    "اردیبهشت",
    "خرداد",
    "تیر",
    "مرداد",
    "شهریور",
    "مهر",
    "آبان",
    "آذر",
    "دی",
    "بهمن",
    "اسفند",
  ];

  const daysOfWeek = [
    "ش",
    "ی",
    "د",
    "س",
    "چ",
    "پ",
    "ج",
  ];

  // =========================================
  // Sync value from parent
  // =========================================

  useEffect(() => {
    if (!value) {
      setSelectedDate("");
      return;
    }

    // Convert ISO date (YYYY-MM-DD) to Jalali for display
    let jalaliValue = value;
    try {
      // Check if value is in ISO format (contains - and first part is 4 digits)
      if (value.includes('-') && value.split('-')[0].length === 4) {
        const pd = new PersianDate(value, 'gregorian');
        jalaliValue = pd.format('YYYY/MM/DD');
      }
    } catch (error) {
      console.error("Date format conversion error:", error);
    }

    setSelectedDate(jalaliValue);

    try {
      const pd = new PersianDate(jalaliValue);

      setCurrentMonth({
        year: pd.year(),
        month: pd.month(),
      });
    } catch (error) {
      console.error(
        "JalaliDatePicker value error:",
        error
      );
    }
  }, [value]);

  // =========================================
  // Close popup when clicking outside
  // =========================================

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target) &&
        popupRef.current &&
        !popupRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  // =========================================
  // Days in month
  // =========================================

  const getDaysInMonth = (year, month) => {
    try {
      const pd = new PersianDate([
        year,
        month,
        1,
      ]);

      return pd.daysInMonth();
    } catch (error) {
      console.error(
        "GET DAYS IN MONTH ERROR:",
        error
      );

      return 30;
    }
  };

  // =========================================
  // First day of week
  // =========================================

  const getFirstDayOfWeek = (year, month) => {
    try {
      const pd = new PersianDate([
        year,
        month,
        1,
      ]);

      return pd.day();
    } catch (error) {
      console.error(
        "GET FIRST DAY ERROR:",
        error
      );

      return 0;
    }
  };

  // =========================================
  // Previous month
  // =========================================

  const prevMonth = () => {
    setCurrentMonth((prev) => {
      if (prev.month === 1) {
        return {
          year: prev.year - 1,
          month: 12,
        };
      }

      return {
        year: prev.year,
        month: prev.month - 1,
      };
    });
  };

  // =========================================
  // Next month
  // =========================================

  const nextMonth = () => {
    setCurrentMonth((prev) => {
      if (prev.month === 12) {
        return {
          year: prev.year + 1,
          month: 1,
        };
      }

      return {
        year: prev.year,
        month: prev.month + 1,
      };
    });
  };

  // =========================================
  // Create Jalali date string
  // =========================================

  const createJalaliDate = (
    year,
    month,
    day
  ) => {
    return `${year}/${String(month).padStart(
      2,
      "0"
    )}/${String(day).padStart(2, "0")}`;
  };

  // Convert Jalali date to ISO format (YYYY-MM-DD) for API
  const toISODate = (jalaliDateStr) => {
    try {
      const pd = new PersianDate(jalaliDateStr);
      const gregorian = pd.toGregorian();
      return `${gregorian.year}-${String(gregorian.month).padStart(2, '0')}-${String(gregorian.day).padStart(2, '0')}`;
    } catch (error) {
      return jalaliDateStr;
    }
  };

  // =========================================
  // Select day
  // =========================================

  const selectDay = (day) => {
    const jalaliDate = createJalaliDate(
      currentMonth.year,
      currentMonth.month,
      day
    );

    console.log(
      "SELECTED JALALI DATE:",
      jalaliDate
    );

    setSelectedDate(jalaliDate);

    if (onChange) {
      onChange(toISODate(jalaliDate));
    }

    setIsOpen(false);
  };

  // =========================================
  // Today
  // =========================================

  const today = new PersianDate();

  const todayString = createJalaliDate(
    today.year(),
    today.month(),
    today.date()
  );

  // =========================================
  // Calendar
  // =========================================

  const daysInMonth = getDaysInMonth(
    currentMonth.year,
    currentMonth.month
  );

  const firstDayOfWeek = getFirstDayOfWeek(
    currentMonth.year,
    currentMonth.month
  );

  const dayElements = [];

  // Empty cells
  for (
    let i = 0;
    i < firstDayOfWeek;
    i++
  ) {
    dayElements.push(
      <div
        key={`empty-${i}`}
        className="day empty"
      />
    );
  }

  // Days
  for (
    let day = 1;
    day <= daysInMonth;
    day++
  ) {
    const dayString = createJalaliDate(
      currentMonth.year,
      currentMonth.month,
      day
    );

    const isSelected =
      dayString === selectedDate;

    const isToday =
      dayString === todayString;

    dayElements.push(
      <div
        key={day}
        className={`day ${
          isSelected ? "selected" : ""
        } ${
          isToday ? "today" : ""
        }`}
        onClick={() => selectDay(day)}
      >
        {day}
      </div>
    );
  }

  // =========================================
  // Display value
  // =========================================

  let displayValue = "";

  if (selectedDate) {
    try {
      displayValue = new PersianDate(
        selectedDate
      ).format("YYYY/MM/DD");
    } catch (error) {
      displayValue = selectedDate;
    }
  }

  // =========================================
  // Render
  // =========================================

  return (
    <div
      className="input-card"
      ref={inputRef}
    >
      {label && (
        <label className="input-label">
          {label}
        </label>
      )}

      <div className="date-input-wrapper">
        <input
          type="text"
          className="date-input"
          value={displayValue}
          placeholder={placeholder}
          readOnly
          onClick={() =>
            !disabled &&
            setIsOpen(true)
          }
          disabled={disabled}
        />

        <svg
          className="calendar-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect
            x="3"
            y="4"
            width="18"
            height="18"
            rx="2"
          />

          <line
            x1="16"
            y1="2"
            x2="16"
            y2="6"
          />

          <line
            x1="8"
            y1="2"
            x2="8"
            y2="6"
          />

          <line
            x1="3"
            y1="10"
            x2="21"
            y2="10"
          />
        </svg>
      </div>

      {isOpen && (
        <div
          className="datepicker-popup"
          ref={popupRef}
        >
          {/* Header */}

          <div className="datepicker-header">
            <button
              className="nav-btn"
              onClick={nextMonth}
              type="button"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>

            <div className="month-year">
              <span>
                {
                  months[
                    currentMonth.month - 1
                  ]
                }
              </span>

              <span>
                {currentMonth.year}
              </span>
            </div>

            <button
              className="nav-btn"
              onClick={prevMonth}
              type="button"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          </div>

          {/* Weekdays */}

          <div className="weekdays">
            {daysOfWeek.map((day) => (
              <div
                key={day}
                className="weekday"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Days */}

          <div className="days">
            {dayElements}
          </div>
        </div>
      )}
    </div>
  );
}

export default JalaliDatePicker;